import React, { useState, useMemo, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Styles } from './styles';
import { IPropertyPhoto } from '@stores/Property/types';
import { ImageModal } from '@components/ImageModal';
import {
  FlipDirection,
  RotationDegrees,
  WatermarkPosition
} from '@utils/image';

// Item sortable individual
interface SortableImageItemProps {
  foto: IPropertyPhoto;
  photoIndex: number;
  isCover: boolean;
  onDeleteImage: (index: number, uri: string) => void;
  onImageClick: (index: number) => void;
}

const SortableImageItem: React.FC<SortableImageItemProps> = ({
  foto,
  photoIndex,
  isCover,
  onDeleteImage,
  onImageClick
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: foto.id ?? `${foto.uri}-${photoIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1
  };

  const handleClick = () => {
    // Só abre o modal se não estiver arrastando
    if (!isDragging) {
      onImageClick(photoIndex);
    }
  };

  const previewUrl = useMemo(
    () => (foto.file ? URL.createObjectURL(foto.file) : null),
    [foto.file]
  );

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <Styles.ImageCard
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}>
      <Styles.ImagePreview
        src={previewUrl ?? foto.uri}
        alt={foto.legenda || `Imagem ${photoIndex + 1}`}
      />
      <Styles.IndexCaption $isCover={isCover}>
        {isCover ? `#${photoIndex + 1} - Capa` : `#${photoIndex + 1}`}
      </Styles.IndexCaption>
      <Styles.DeleteButton
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDeleteImage(photoIndex, foto.uri);
        }}>
        &times;
      </Styles.DeleteButton>
    </Styles.ImageCard>
  );
};

const MemoizedSortableImageItem = React.memo(SortableImageItem);

export interface ImageUploaderProps {
  photos: IPropertyPhoto[];
  onAddImages: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteImage: (index: number, uri: string) => void;
  onSortEnd?: (oldIndex: number, newIndex: number) => void;
  watermarkPositions?: Map<number, WatermarkPosition>;
  watermarkOpacities?: Map<number, number>;
  watermarkSizes?: Map<number, number>;
  onWatermarkPositionChange?: (
    photoId: number,
    position: WatermarkPosition
  ) => Promise<void>;
  onWatermarkOpacityChange?: (
    photoId: number,
    opacity: number
  ) => Promise<void>;
  onWatermarkSizeChange?: (
    photoId: number,
    relativeWidth: number
  ) => Promise<void>;
  onRotateImage?: (photoId: number, degrees: RotationDegrees) => Promise<void>;
  onFlipImage?: (photoId: number, direction: FlipDirection) => Promise<void>;
  onCaptionChange?: (index: number, caption: string) => void;
  label?: string;
}

const ImageUploaderComponent: React.FC<ImageUploaderProps> = ({
  photos,
  onAddImages,
  onDeleteImage,
  onSortEnd,
  watermarkPositions,
  watermarkOpacities,
  watermarkSizes,
  onWatermarkPositionChange,
  onWatermarkOpacityChange,
  onWatermarkSizeChange,
  onRotateImage,
  onFlipImage,
  onCaptionChange,
  label = 'Imagens:'
}) => {
  const sortedPhotos = useMemo(
    () => photos.slice().sort((a, b) => a.ordem - b.ordem),
    [photos]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const imageUrls = useMemo(
    () =>
      sortedPhotos.map((foto) =>
        foto.file ? URL.createObjectURL(foto.file) : foto.uri
      ),
    [sortedPhotos]
  );

  useEffect(() => {
    return () => {
      imageUrls.forEach((url, index) => {
        if (sortedPhotos[index]?.file) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imageUrls, sortedPhotos]);

  const imageCaptions = useMemo(
    () => sortedPhotos.map((foto) => foto.legenda || ''),
    [sortedPhotos]
  );

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // Requer 8px de movimento antes de iniciar o drag
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedPhotos.findIndex(
        (foto) =>
          (foto.id ?? `${foto.uri}-${sortedPhotos.indexOf(foto)}`) === active.id
      );
      const newIndex = sortedPhotos.findIndex(
        (foto) =>
          (foto.id ?? `${foto.uri}-${sortedPhotos.indexOf(foto)}`) === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1 && onSortEnd) {
        onSortEnd(oldIndex, newIndex);
      }
    }
  };

  const getItemId = (foto: IPropertyPhoto, index: number) =>
    foto.id ?? `${foto.uri}-${index}`;

  return (
    <Styles.ImageUploadSection>
      <Styles.Label>{label}</Styles.Label>
      <Styles.ImageInputContainer>
        <Styles.UploadButton>
          <input
            type="file"
            multiple
            accept="image/*"
            style={{ display: 'none' }}
            id="image-upload"
            onChange={onAddImages}
          />
          <Styles.UploadLabel htmlFor="image-upload">
            Selecione
          </Styles.UploadLabel>
        </Styles.UploadButton>
      </Styles.ImageInputContainer>

      {/* Galeria de imagens existentes + recém adicionadas */}
      {photos && photos.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}>
          <SortableContext
            items={sortedPhotos.map((foto, index) => getItemId(foto, index))}
            strategy={rectSortingStrategy}>
            <Styles.ImageGalleryGrid>
              {sortedPhotos.map((foto, index) => {
                const isCover = index === 0;
                return (
                  <MemoizedSortableImageItem
                    key={getItemId(foto, index)}
                    foto={foto}
                    photoIndex={index}
                    isCover={isCover}
                    onDeleteImage={onDeleteImage}
                    onImageClick={handleImageClick}
                  />
                );
              })}
            </Styles.ImageGalleryGrid>
          </SortableContext>
        </DndContext>
      )}

      <ImageModal
        images={imageUrls}
        currentIndex={currentImageIndex}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onNavigate={setCurrentImageIndex}
        photoIds={sortedPhotos.map((foto) => foto.id)}
        watermarkPositions={watermarkPositions}
        watermarkOpacities={watermarkOpacities}
        watermarkSizes={watermarkSizes}
        onWatermarkPositionChange={onWatermarkPositionChange}
        onWatermarkOpacityChange={onWatermarkOpacityChange}
        onWatermarkSizeChange={onWatermarkSizeChange}
        onRotateImage={onRotateImage}
        onFlipImage={onFlipImage}
        captions={imageCaptions}
        onCaptionChange={onCaptionChange}
      />
    </Styles.ImageUploadSection>
  );
};

export const ImageUploader = React.memo(ImageUploaderComponent);

export default ImageUploader;
